import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { getCanonicalTypeInfo, toggleSingleItemSelection } from '../../../data/analyticsDataSlice';
import { selectEntityViewTemplateForAsset } from '../../../data/templateSlice';
import { TpCanonicalTypeInfo, TpDataContext } from '../../../data/interfaces';
import { hasSelectedItems, isSelectedItem } from '../helpers/misc';
import styles from './SingleItemView.module.scss';
import stylesForm from '../../../styles/lightBackgroundLargeForm.module.scss';
import { SingleItemViewFactory } from './SingleItemViewFactory';
import { EntityView, SingleEntityTemplate, TpTemplateInfo } from '../../../api/models/assets';
import { SectionsIndexWrapper, getSectionsIndexContext } from '../../../components/sections-index/SectionsIndex';
import { TpSingleEntityViewContext } from './interfaces';
import { useState } from 'react';
import { TpDashboardContext } from '../dashboard/interfaces';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { parse as yamlParse } from 'yaml';
import { getDataTableByCanonicalType } from '../charts/chartDataBindings';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';

interface TpSingleItemViewProps {
  assetInfo: TpAssetInfo;
  dataContext: TpDataContext;
  dashboardContext: TpDashboardContext;
  canonicalTypeId: string;
  itemId: string;
  closeEntityPopup: () => void;
  isAssetTemplate?: boolean;
}

/**
 * Gets the title to be displayed for an entity in the single item view, based on the provided title property in the template.
 */
function getEntityTitle(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  itemId: string,
  titleProperty?: string,
): string {
  if (!titleProperty) return itemId;

  const itemIdx = canonicalTypeInfo.itemIdList.indexOf(itemId);
  if (itemIdx === -1) return itemId;

  const titleVal = getDataTableByCanonicalType(dataContext, canonicalTypeInfo.id)?.columnData?.[titleProperty]?.[
    itemIdx
  ];
  return titleVal || itemId;
}

export function SingleItemView({
  assetInfo,
  dataContext,
  dashboardContext,
  canonicalTypeId,
  itemId,
  closeEntityPopup,
  isAssetTemplate,
}: TpSingleItemViewProps) {
  // When a user clicks on a linked entity in this single entity view, we support navigstinh to that
  // To be able to go back, we maintain a stack of nagivated entitoies
  // NOTE: in the future we may want to re-implement this via routing and url state management
  const assetType = assetInfo.type;
  const [itemOpenedStack, setItemOpenedStack] = useState([{ canonicalTypeId: canonicalTypeId, itemId: itemId }]);
  const dispatch = useAppDispatch();

  const currenrCanonicalTypeId = itemOpenedStack[itemOpenedStack.length - 1].canonicalTypeId;
  const currentItemId = itemOpenedStack[itemOpenedStack.length - 1].itemId;

  const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, currenrCanonicalTypeId);
  const selectedItemsMap = canonicalTypeInfo.selectedItemsMap;
  const hasSelection = hasSelectedItems(selectedItemsMap);

  function onToggleSingleItemSelection(itemId: string) {
    dispatch(
      toggleSingleItemSelection({ dataContextId: dataContext.id, canonicalTypeId: currenrCanonicalTypeId, itemId }),
    );
  }

  const singleEntityViewContext: TpSingleEntityViewContext = {
    dashboardContext: dashboardContext,
    openEntity: (canonicalTypeId: string, entityId: string) => {
      setItemOpenedStack([...itemOpenedStack, { canonicalTypeId, itemId: entityId }]);
    },
    closeEntityPopup,
  };
  const entityViewTemplate: TpTemplateInfo | undefined = useAppSelector((state) =>
    selectEntityViewTemplateForAsset(state, assetType, isAssetTemplate ? assetInfo.id : undefined),
  );
  if (!entityViewTemplate) {
    showErrorMessage(`No entity view template found for asset type ${assetType}. Please contact your administrator.`);
    return null;
  }
  const parsedEntityView = yamlParse(entityViewTemplate.yamlString) as any;
  const entityTemplateContent = (parsedEntityView?.templateContent ?? parsedEntityView) as EntityView;

  const entityTypeTemplate = entityTemplateContent.entities.find(
    (entity: SingleEntityTemplate) => entity.entityType == currenrCanonicalTypeId,
  );

  const label = getEntityTitle(dataContext, canonicalTypeInfo, currentItemId, entityTypeTemplate?.titleProperty);

  let selIcon = (
    <span style={{ color: 'var(--base-color)', opacity: 0.5, display: 'inline-block', paddingTop: '3px' }}>
      <FontAwesomeIcon icon={['fas', 'circle']} />
    </span>
  );
  if (hasSelection) {
    selIcon = (
      <span style={{ opacity: 0.1, display: 'inline-block', paddingTop: '3px' }}>
        <FontAwesomeIcon icon={['fas', 'circle']} />
      </span>
    );
    if (isSelectedItem(selectedItemsMap, currentItemId))
      selIcon = (
        <span style={{ color: 'var(--base-color)', display: 'inline-block', paddingTop: '3px' }}>
          <FontAwesomeIcon icon={['fas', 'circle-check']} />
        </span>
      );
  }

  const sectionIndexContext = getSectionsIndexContext(
    `_entity_${dataContext.id}_${currenrCanonicalTypeId}_${currentItemId}`,
  );

  return (
    <>
      <SectionsIndexWrapper sectionIndexContext={sectionIndexContext}>
        <div className={styles.wrapper}>
          <div className={stylesForm.backGround}>
            <div className={styles.itemTitle}>
              {label}
              <div
                className={styles.selectionIcon}
                onClick={(ev) => {
                  onToggleSingleItemSelection(currentItemId);
                  ev.stopPropagation();
                }}
              >
                {selIcon}
              </div>
            </div>
            {entityTypeTemplate && (
              <SingleItemViewFactory
                dataContext={dataContext}
                entityTypeTemplate={entityTypeTemplate}
                itemId={currentItemId}
                sectionIndexContext={sectionIndexContext}
                singleEntityViewContext={singleEntityViewContext}
              />
            )}
          </div>
        </div>
      </SectionsIndexWrapper>
      {itemOpenedStack.length > 1 && (
        <div
          className={styles.backButton}
          onClick={() => {
            setItemOpenedStack(itemOpenedStack.slice(0, -1));
          }}
        >
          <FontAwesomeIcon icon="circle-arrow-left" />
        </div>
      )}
    </>
  );
}
